import type { GameEvent, Variant } from "@app/protocol";
import type { baloot, GameView, hearts, king, pisti, spades, tarneeb } from "@app/rules";
import type { ToastData } from "../components/Toast";
import { balootHandEnd, balootSeatSub } from "../routes/balootText";
import {
  batakHandEnd,
  biddingSeatSub,
  tarneebHandEnd,
  trumpPickToast,
} from "../routes/biddingText";
import { heartsEventText, heartsHandEnd, heartsSeatSub } from "../routes/heartsText";
import { kingEventText, kingHandEnd, kingSeatSub } from "../routes/kingText";
import { pistiSeatSub } from "../routes/pistiText";
import { spadesHandEnd, spadesSeatSub } from "../routes/spadesText";
import type { T } from "../routes/tableText";
import type { Seats } from "../routes/toastParts";
import type { AnyView } from "./types";

/**
 * A game in words (review 0027 B2): what the shared table says on a game's behalf. No components
 * here, so the text code and its tests stay free of the browser; the panels are in `index.tsx`.
 * The record is exhaustive: a game on the wire without its words does not compile.
 */

// Methods, not function properties: words written for one game's view stand in the record of all
// of them, and `wordsOf` hands them only views of their own game.
export interface GameWords<V extends AnyView = AnyView> {
  /** A seat's line under its name. */
  seatSub(view: V, seat: number, yourSeat: number | null, t: T): string;
  /** Between hands, in the game's own words. */
  handEnd(view: V, seats: Seats, yourSeat: number | null, t: T): ToastData | null;
  /** The game's own sentence for an event; the shared sentences follow when it has none. */
  eventText?(last: GameEvent, view: V, seats: Seats, t: T): string | null;
  /** Under a team's score in the top bar. */
  teamNote?(view: V, team: number, t: T): string;
  /** A toast about the move that is yours now (the bid you won: name the trump). */
  turnToast?(view: V, yourSeat: number | null, t: T): ToastData | null;
}

const batak: GameWords<GameView> = {
  seatSub: biddingSeatSub,
  handEnd: (view, seats, _you, t) => batakHandEnd(view, seats, t),
  turnToast: trumpPickToast,
};

const tarneebWords: GameWords<tarneeb.TarneebView> = {
  seatSub: biddingSeatSub,
  handEnd: (view, seats, _you, t) => tarneebHandEnd(view, seats, t),
  turnToast: trumpPickToast,
};

const spadesWords: GameWords<spades.SpadesView> = {
  seatSub: spadesSeatSub,
  handEnd: (view, _seats, _you, t) => spadesHandEnd(view, t),
  teamNote: (view, team, t) => t("spades.bags", { bags: view.teamBags[team] ?? 0 }),
};

const balootWords: GameWords<baloot.BalootView> = {
  seatSub: balootSeatSub,
  handEnd: (view, seats, _you, t) => balootHandEnd(view, seats, t),
};

const heartsWords: GameWords<hearts.HeartsView> = {
  seatSub: heartsSeatSub,
  handEnd: heartsHandEnd,
  eventText: heartsEventText,
};

const kingWords: GameWords<king.KingView> = {
  seatSub: kingSeatSub,
  handEnd: kingHandEnd,
  eventText: (last, _view, seats, t) => kingEventText(last, seats, t),
};

const pistiWords: GameWords<pisti.PistiView> = {
  seatSub: pistiSeatSub,
  // No toast between decks: the count stands in the middle of the table, where the toast would lie.
  handEnd: () => null,
};

type ViewOf<K extends Variant> = Extract<AnyView, { variant: K }>;

const WORDS: { readonly [K in Variant]: GameWords<ViewOf<K>> } = {
  batak,
  spades: spadesWords,
  tarneeb: tarneebWords,
  baloot: balootWords,
  hearts: heartsWords,
  king: kingWords,
  pisti: pistiWords,
};

/** The words of the game this view belongs to. */
export function wordsOf(view: AnyView): GameWords {
  return WORDS[view.variant] as GameWords;
}
